import { useExperience } from '@unisporkal/experiences';
import useBatchDownload from '../../../../hooks/queries/useBatchDownload';
import useOnLoad from '../../../../hooks/queries/useOnLoad';
import { type NotDownloadedAssetsData } from '../types';

const QUOTA_HAS_BEEN_MET = 'QuotaHasBeenMet';

type UnauthorizedAsset = { assetId: string; reason?: string };

const useNotDownloadedAssets = (): NotDownloadedAssetsData | null => {
  const { response: onLoadResponse } = useOnLoad() as {
    response?: {
      config?: {
        unauthorizedAssets?: UnauthorizedAsset[];
        batchAgreements?: Array<{
          authorizedAssets?: Array<{ assetId: string }>;
        }>;
      };
    };
  };
  const { response: batchDownloadResponse } = useBatchDownload() as {
    response?: { unauthorizedAssets?: UnauthorizedAsset[] };
  };

  // Pre-check and create-download each know about assets the other can't:
  // pre-check catches assets never sent to create-download at all, while
  // create-download catches ones that failed after pre-check authorized them.
  //
  // Pre-check's own unauthorizedAssets can list an asset its own
  // batchAgreements separately authorizes, so exclude those too.
  const preCheckAuthorizedAssetIds = new Set(
    (onLoadResponse?.config?.batchAgreements ?? []).flatMap(
      (agreement) =>
        agreement.authorizedAssets?.map((asset) => asset.assetId) ?? []
    )
  );
  const preCheckUnauthorizedAssets = (
    onLoadResponse?.config?.unauthorizedAssets ?? []
  ).filter((asset) => !preCheckAuthorizedAssetIds.has(asset.assetId));
  const postDownloadUnauthorizedAssets =
    batchDownloadResponse?.unauthorizedAssets ?? [];

  const unauthorizedAssetsById = new Map<string, UnauthorizedAsset>();
  [...preCheckUnauthorizedAssets, ...postDownloadUnauthorizedAssets].forEach(
    (asset) => unauthorizedAssetsById.set(asset.assetId, asset)
  );
  const unauthorizedAssets = Array.from(unauthorizedAssetsById.values());

  const isMultiPaBulkDownloadActive = useExperience(
    'MultiPABulkDownload'
  ).isActive();

  if (!isMultiPaBulkDownloadActive || !unauthorizedAssets.length) return null;

  return {
    assetIds: unauthorizedAssets.map((asset) => asset.assetId),
    isQuotaExceeded: unauthorizedAssets.every(
      (asset) => asset.reason === QUOTA_HAS_BEEN_MET
    ),
  };
};

export default useNotDownloadedAssets;
